import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = '/api';

const getPasswordStrength = (password) => {
  let score = 0;
  if (!password) return { label: 'Poor', color: 'bg-red-400', score };
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { label: 'Poor', color: 'bg-red-400', score };
  if (score === 2) return { label: 'Weak', color: 'bg-yellow-400', score };
  if (score === 3) return { label: 'Good', color: 'bg-blue-400', score };
  return { label: 'Strong', color: 'bg-green-500', score };
};

const PasswordResetModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));

  useEffect(() => {
    let interval;
    if (step === 2 && timer > 0) {
      interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setEmail('');
      setOtp(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setMessage('');
      setLoading(false);
      setTimer(300);
      setIsResendDisabled(true);
    }
  }, [isOpen]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/forgot-password`, { email });
      setMessage(data.message);
      setStep(2);
      setTimer(300);
      setIsResendDisabled(true);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (idx, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    if (value && idx < 5) {
      otpRefs.current[idx + 1].current.focus();
    }
    if (!value && idx > 0) {
      otpRefs.current[idx - 1].current.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 6).split('');
    if (pasted.length === 6) {
      setOtp(pasted);
      otpRefs.current[5].current.focus();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (otp.some(d => d === '')) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/reset-password`, {
        email,
        otp: otp.join(''),
        newPassword,
        confirmPassword
      });
      setMessage(data.message + ' You can now log in with your new password.');
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`${API_URL}/forgot-password`, { email });
      setMessage(data.message);
      setTimer(300);
      setIsResendDisabled(true);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0].current.focus();
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail('');
    setOtp(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
    setLoading(false);
    setTimer(300);
    setIsResendDisabled(true);
    onClose();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  const canSubmit =
    email &&
    otp.every(d => d) &&
    newPassword &&
    confirmPassword &&
    passwordsMatch &&
    passwordStrength.score >= 2 &&
    !loading;

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-2 sm:px-4">
    <div className="bg-white rounded-lg shadow-2xl p-4 sm:p-6 md:p-8 w-full max-w-sm sm:max-w-md relative animate-fadeIn max-h-[95vh] overflow-y-auto">
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-500 hover:text-gray-800 text-xl sm:text-2xl"
      >
        &times;
      </button>

      {/* Heading */}
      <h2 className="text-xl sm:text-2xl font-bold text-center text-[#2b6777] mb-4 sm:mb-6">
        Reset Password
      </h2>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 text-red-700 p-2 sm:p-3 rounded-md mb-3 sm:mb-4 text-center animate-shake text-sm sm:text-base">
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-100 text-green-700 p-2 sm:p-3 rounded-md mb-3 sm:mb-4 text-center animate-fadeIn text-sm sm:text-base">
          {message}
        </div>
      )}

      {/* Step 1 - Email Form */}
      {step === 1 && (
        <form onSubmit={handleSendOtp} className="flex flex-col gap-3 sm:gap-4">
          <p className="text-center text-gray-600 text-sm sm:text-base">
            Enter your email address and we'll send you an OTP to reset your password.
          </p>
          <input
            type="email"
            placeholder="Your Email Address"
            className="w-full border border-[#c8d8e4] rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-[#52ab98] text-sm sm:text-base"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete="off"
          />
          <button
            type="submit"
            className="bg-[#2b3577] hover:bg-[#52ab98] text-white font-semibold py-2 rounded-lg sm:rounded-xl transition text-sm sm:text-lg disabled:opacity-60"
            disabled={loading || !email}
          >
            {loading ? 'Sending...' : 'Send OTP'}
          </button>
        </form>
      )}

      {/* Step 2 - OTP + Reset Form */}
      {step === 2 && (
        <form onSubmit={handleResetPassword} className="flex flex-col gap-3 sm:gap-4">
          <p className="text-center text-gray-600 text-sm sm:text-base">
            An OTP has been sent to <strong>{email}</strong>. Please enter it below.
          </p>

          {/* OTP Inputs */}
          <div
            className="flex justify-center gap-1 sm:gap-2 mb-2"
            onPaste={handleOtpPaste}
            autoComplete="off"
          >
            {otp.map((digit, idx) => (
              <input
                key={idx}
                ref={otpRefs.current[idx]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                className="w-8 h-10 sm:w-10 sm:h-12 text-center text-lg sm:text-xl border border-[#c8d8e4] rounded focus:ring-2 focus:ring-[#52ab98] transition-all outline-none"
                value={digit}
                onChange={e => handleOtpChange(idx, e.target.value)}
                onFocus={e => e.target.select()}
                autoComplete="off"
                disabled={loading}
                style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
              />
            ))}
          </div>

          {/* Passwords */}
          <input
            type="password"
            placeholder="New Password"
            className="w-full border border-[#c8d8e4] rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-[#52ab98] text-sm sm:text-base"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
          />

          {/* Strength Bar */}
          <div className="flex items-center gap-2 mb-1">
            <div className={`h-2 w-20 sm:w-24 rounded transition-all ${passwordStrength.color}`}></div>
            <span className={`text-[10px] sm:text-xs font-semibold px-2 py-1 rounded-xl ${passwordStrength.color}`}>
              {passwordStrength.label}
            </span>
          </div>

          <input
            type="password"
            placeholder="Confirm New Password"
            className={`w-full border rounded px-3 py-2 sm:px-4 sm:py-2 focus:outline-none focus:ring-2 focus:ring-[#52ab98] text-sm sm:text-base ${
              confirmPassword && (passwordsMatch ? 'border-green-400' : 'border-red-400 animate-shake')
            }`}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
          />

          {confirmPassword && (
            <div className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold">
              {passwordsMatch ? (
                <span className="text-green-600 animate-fadeIn">✔ Passwords match</span>
              ) : (
                <span className="text-red-600 animate-shake">✖ Passwords do not match</span>
              )}
            </div>
          )}

          <div className="text-center text-xs sm:text-sm text-gray-500">
            {timer > 0 ? `Time remaining: ${formatTime(timer)}` : 'OTP expired.'}
          </div>

          {/* Buttons */}
          <button
            type="submit"
            className={`bg-[#2b3577] hover:bg-[#52ab98] text-white font-semibold py-2 rounded-lg sm:rounded-xl transition text-sm sm:text-lg disabled:opacity-60 ${canSubmit ? 'animate-fadeIn' : ''}`}
            disabled={!canSubmit}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <button
            type="button"
            onClick={handleResendOtp}
            className="text-center text-xs sm:text-sm text-[#2b6777] cursor-pointer hover:underline disabled:text-gray-400 disabled:cursor-not-allowed mt-1 sm:mt-2"
            disabled={isResendDisabled || loading}
          >
            Resend OTP
          </button>

          <button
            type="button"
            onClick={() => setStep(1)}
            className="text-center text-xs sm:text-sm text-gray-500 cursor-pointer hover:underline mt-1 sm:mt-2"
            disabled={loading}
          >
            Use a different email address
          </button>
        </form>
      )}
    </div>
  </div>
);

};

export default PasswordResetModal; 